import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, Flex, FormControl, Input, Text, useToast, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import passwordLock from '../../create-password.png';
import logo from '../../PINS_logo_197x79.png';
import { createErrorToast, createSuccessToast } from '../../redux/modules/app';
import {
  createUserPassword,
  selectInvalidUser,
  selectPasswordFieldsLoading, selectPasswordUser,
  validateUserToCreatePassword
} from '../../redux/modules/createPassword';
import { schema } from '../../utils/passwordSchema';

import ErrorMessage from './ErrorMessage';
import PasswordInputGroup from './PasswordInputGroup';

function CreatePasswordPage() {
  const dispatch = useDispatch();
  const { userEmail, passwordKey } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const loadingPasswordFields = useSelector(selectPasswordFieldsLoading);
  const invalidUser = useSelector(selectInvalidUser);
  const user = useSelector(selectPasswordUser);
  const baseUrl = window.location.origin.toString();

  useEffect(() => {
    dispatch(validateUserToCreatePassword(userEmail, passwordKey));
  }, [dispatch, passwordKey, userEmail]);

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema)
  });

  const onSubmit = async data => {
    const response = await dispatch(createUserPassword(user.userId, data.password, passwordKey));

    if (response) {
      dispatch(createSuccessToast({
        title: 'Password created successfully!',
        description: 'Taking you to the login page now.  Welcome to PINS!'
      }));

      setTimeout(() => {
        window.location = `${baseUrl}/login`;
      }, 5000);

      reset();
    } else {
      dispatch(createErrorToast({
        title: 'Uh oh. Something went wrong',
        description: 'Please try again by resetting your browser or contact support at support@pinsadvantage.com'
      }));
    }
  };

  return (
    <Box
      bgColor="primaryScale.50"
      color="primaryScale.700"
      h="100vh"
      p={6}
      w="100vw"
    >
      <Flex direction="row" justifyContent="space-between">
        <a href={`${baseUrl}/`}>
          <img alt="PINS Advantage Logo" src={logo} />
        </a>
        <Button
          textStyle="bodyXl"
          variant="primary"
          onClick={() => { window.location = `${baseUrl}/login`; }}
        >
          Login/Register
        </Button>
      </Flex>
      <Box h="calc(100vh - 120px)" pl="8%" pr="8%" pt="7%">
        <Text textAlign="right" textStyle="displayXl">
          Welcome to PINS Advantage!
        </Text>
        <Flex direction="row" justifyContent="space-between" pt={8}>
          <Box maxW="615px" minW="275px" mr="8%">
            <Flex align="center" h="100%" w="100%">
              <img alt="Create Password" src={passwordLock} />
            </Flex>
          </Box>
          <form id="passwordForm" onSubmit={handleSubmit(onSubmit)}>
            <VStack minW="300px" pt="55px" spacing={10} textAlign="left" w="475px">
              <Text
                textStyle="displayXs600"
                w="100%"
              >
                Create Password
              </Text>
              { loadingPasswordFields
                ? (
                  <Text>Loading...</Text>
                )
                : (
                  <>
                    { invalidUser
                      ? (
                        <>
                          <Text>Oops!  Looks like either this link has expired or your email is invalid.</Text>
                          <p>
                            {'Please contact your admin or '}
                            <a
                              href="mailto:support@pinsadvantage.com"
                              style={{ textDecoration: 'underline' }}
                            >
                              reach out to support.
                            </a>
                          </p>
                        </>
                      )
                      : (
                        <>
                          <FormControl>
                            <PasswordInputGroup
                              label="Password"
                              show={showPassword}
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <Input
                                name="password"
                                {...register('password')}
                                _focus={{ outline: 'none', borderColor: 'primaryScale.600', borderWidth: '1px' }}
                                bg="white"
                                borderColor={errors.password ? 'error.primary.600' : 'transparent'}
                                boxShadow="md"
                                color="greyScale.800"
                                type={showPassword ? 'text' : 'password'}
                              />
                            </PasswordInputGroup>
                            {errors.password && (
                            <ErrorMessage errorMessage={errors.password.message} />
                            ) }
                          </FormControl>
                          <FormControl>
                            <PasswordInputGroup
                              label="Confirm Password"
                              show={showConfirmPassword}
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              <Input
                                name="confirmPassword"
                                {...register('confirmPassword')}
                                _focus={{ outline: 'none', borderColor: 'primaryScale.600', borderWidth: '1px' }}
                                bg="white"
                                borderColor={errors.confirmPassword ? 'error.primary.600' : 'transparent'}
                                boxShadow="md"
                                color="greyScale.800"
                                type={showConfirmPassword ? 'text' : 'password'}
                              />
                            </PasswordInputGroup>
                            {errors.confirmPassword && (
                            <ErrorMessage errorMessage={errors.confirmPassword.message} />
                            ) }
                          </FormControl>
                          <Button
                            _active={{ bg: 'linear-gradient(135deg, #00B96D 0%, #39B8FF 90%)' }}
                            _hover={{ bgColor: 'success.primary.900' }}
                            bg="success.primary.600"
                            form="passwordForm"
                            type="submit"
                          >
                            Set Password
                          </Button>
                        </>
                      )}
                  </>
                )}
            </VStack>
          </form>
        </Flex>
      </Box>
    </Box>
  );
}

export default CreatePasswordPage;
